import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { StripePayoutPreviewResponse, getStripePayoutPreview } from 'src/shared/services/accountingIntegrationService';

interface StripePayoutPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  ownerId: number;
}

/**
 * Visar vad som skulle bokföras för Stripes utbetalningar, utan att något bokförs.
 *
 * Anropet läser bara: det hämtar utbetalningarna från Stripe och visar beloppen. Varken Fortnox
 * eller databasen rörs, så dialogen går att öppna mot en kund i drift för att se vad som väntar
 * innan funktionen slås på.
 */
export const StripePayoutPreviewDialog = ({ open, onClose, ownerId }: StripePayoutPreviewDialogProps) => {
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StripePayoutPreviewResponse>();

  const amount = (value: number) => value.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getStripePayoutPreview(ownerId);
      if (!isMounted()) return;
      setData(result.data);
    } catch (err) {
      ConsoleHelper.log(err);
      if (isMounted()) setData(undefined);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [ownerId, isMounted]);

  useEffect(() => {
    if (!open || !ownerId) return;
    fetchPayouts();
  }, [open, ownerId, fetchPayouts]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('invoice:stripePayoutPreview.title')}</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {t('invoice:stripePayoutPreview.info')}
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && !data && <Alert severity='error'>{t('invoice:stripePayoutPreview.failed')}</Alert>}

        {!loading && data && !data.enabled && <Alert severity='info'>{data.reason}</Alert>}

        {!loading && data && data.enabled && data.error && <Alert severity='error'>{data.error}</Alert>}

        {!loading && data && data.enabled && !data.error && (
          <>
            <Stack direction='row' spacing={3} flexWrap='wrap' sx={{ mb: 2 }}>
              <Typography variant='caption' color='text.secondary'>
                {t('invoice:stripePayoutPreview.fromDate')}: {data.fromDate}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {t('invoice:stripePayoutPreview.accounts', {
                  bank: data.bankAccount,
                  fee: data.feeAccount,
                  clearing: data.clearingAccount,
                })}
              </Typography>
            </Stack>

            {data.payouts.length === 0 && <Alert severity='success'>{t('invoice:stripePayoutPreview.none')}</Alert>}

            {data.payouts.length > 0 && (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('invoice:stripePayoutPreview.date')}</TableCell>
                    <TableCell align='right'>{t('invoice:stripePayoutPreview.gross')}</TableCell>
                    <TableCell align='right'>{t('invoice:stripePayoutPreview.fee')}</TableCell>
                    <TableCell align='right'>{t('invoice:stripePayoutPreview.net')}</TableCell>
                    <TableCell>{t('invoice:stripePayoutPreview.note')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.payouts.map((payout) => (
                    <TableRow key={payout.payoutId}>
                      <TableCell>{payout.payoutDate}</TableCell>
                      <TableCell align='right'>{amount(payout.grossAmount)}</TableCell>
                      <TableCell align='right'>{amount(payout.feeAmount)}</TableCell>
                      <TableCell align='right'>{amount(payout.netAmount)}</TableCell>
                      <TableCell>
                        {payout.problem ? (
                          <Typography variant='caption' color='warning.main'>
                            {payout.problem}
                          </Typography>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            {payout.payoutId}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Alert severity='info' sx={{ mt: 2 }}>
              {t('invoice:stripePayoutPreview.totals', {
                count: data.totals.count,
                gross: amount(data.totals.grossAmount),
                fee: amount(data.totals.feeAmount),
                net: amount(data.totals.netAmount),
              })}
              {data.totals.problemCount > 0 && ` ${t('invoice:stripePayoutPreview.problemCount', { count: data.totals.problemCount })}`}
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant='text' onClick={onClose}>
          {t('common:button.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
